import { DatePicker } from '@components/common/DatePicker';
import { useAppSelector } from '@hooks/useRedux';
import { selectAccommodationProperty } from '@store/accommodation';
import { selectOutletId } from '@store/auth/authSelectors';
import { Download, RefreshCw } from 'lucide-react';
import React, { useCallback, useState } from 'react';
import {
  OccupancyReportRow,
  fetchOccupancyReport,
} from '../../../../services/accommodationService';
import { apiError } from '../../../../util/notify';
import { currentMonthRange, exportToCsv, fmtDate } from './reportUtils';

const { from: initFrom, to: initTo } = currentMonthRange();

export const OccupancyReportPage: React.FC = () => {
  const property = useAppSelector(selectAccommodationProperty);
  const userOutletId = useAppSelector(selectOutletId);
  const outletId = property?.outlet_id ?? userOutletId ?? null;

  const [dateFrom, setDateFrom] = useState(initFrom);
  const [dateTo, setDateTo] = useState(initTo);
  const [rows, setRows] = useState<OccupancyReportRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await fetchOccupancyReport(outletId, dateFrom, dateTo);
      setRows(data);
      setLoaded(true);
    } catch (err) {
      apiError(err instanceof Error ? err.message : 'Failed to load report');
    } finally {
      setIsLoading(false);
    }
  }, [outletId, dateFrom, dateTo]);

  const avgOccupancy =
    rows.length > 0
      ? (
          rows.reduce((s, r) => s + (r.occupancy_pct ?? 0), 0) / rows.length
        ).toFixed(1)
      : '—';

  const maxOccupied = rows.reduce((m, r) => Math.max(m, r.rooms_occupied), 0);
  const totalRooms = rows[0]?.total_rooms ?? 0;

  const handleCsv = () => {
    exportToCsv(
      `occupancy-report-${dateFrom}-${dateTo}`,
      rows.map((r) => ({
        Date: r.stay_date,
        'Total Rooms': r.total_rooms ?? 0,
        'Rooms Occupied': r.rooms_occupied,
        'Occupancy %': r.occupancy_pct ?? 0,
      })),
    );
  };

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-gray-200 bg-white p-4">
        <DatePicker
          mode="single"
          id="occ-date-from"
          label="From date"
          value={dateFrom}
          onChange={setDateFrom}
        />
        <DatePicker
          mode="single"
          id="occ-date-to"
          label="To date"
          value={dateTo}
          onChange={setDateTo}
        />
        <button
          type="button"
          onClick={load}
          disabled={isLoading}
          className="flex items-center gap-2 rounded-xl bg-pos-primary px-4 py-2 text-sm font-medium text-white hover:bg-pos-primary/90 disabled:opacity-60"
        >
          <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
          {isLoading ? 'Loading…' : 'Run report'}
        </button>
        {loaded && rows.length > 0 && (
          <button
            type="button"
            onClick={handleCsv}
            className="flex items-center gap-2 rounded-xl border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <Download size={14} />
            Export CSV
          </button>
        )}
      </div>

      {loaded && (
        <>
          {/* KPI cards */}
          <div className="grid gap-4 sm:grid-cols-3">
            {[
              {
                label: 'Days in range',
                value: String(rows.length),
                sub: 'calendar days',
              },
              {
                label: 'Avg. occupancy',
                value: `${avgOccupancy}%`,
                sub: 'per night',
              },
              {
                label: 'Peak occupied',
                value: `${maxOccupied} / ${totalRooms}`,
                sub: 'rooms in a single night',
              },
            ].map((card) => (
              <div
                key={card.label}
                className="rounded-2xl border border-gray-200 bg-white p-4"
              >
                <p className="text-xs text-gray-500">{card.label}</p>
                <p className="mt-1 text-xl font-semibold text-gray-900">
                  {card.value}
                </p>
                <p className="mt-0.5 text-xs text-gray-400">{card.sub}</p>
              </div>
            ))}
          </div>

          {/* Occupancy bar chart */}
          {rows.length > 0 && (
            <div className="rounded-2xl border border-gray-200 bg-white p-5">
              <h4 className="mb-4 text-sm font-semibold text-gray-900">
                Daily occupancy
              </h4>
              <div className="space-y-1.5">
                {rows.map((r) => {
                  const pct = r.occupancy_pct ?? 0;
                  const colour =
                    pct >= 80
                      ? 'bg-green-500'
                      : pct >= 50
                        ? 'bg-pos-primary'
                        : pct >= 20
                          ? 'bg-amber-400'
                          : 'bg-gray-300';
                  return (
                    <div key={r.stay_date} className="flex items-center gap-3">
                      <span className="w-24 shrink-0 text-xs text-gray-500">
                        {fmtDate(r.stay_date)}
                      </span>
                      <div
                        className="flex-1 overflow-hidden rounded-full bg-gray-100"
                        style={{ height: 8 }}
                      >
                        <div
                          className={`h-full rounded-full ${colour} transition-all`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className="w-16 shrink-0 text-right text-xs font-medium text-gray-700">
                        {r.rooms_occupied}/{r.total_rooms ?? '?'} ({pct}%)
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Table */}
          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
            <div className="border-b border-gray-100 px-5 py-3">
              <h4 className="text-sm font-semibold text-gray-900">
                Occupancy detail — {fmtDate(dateFrom)} to {fmtDate(dateTo)}
              </h4>
            </div>
            {rows.length === 0 ? (
              <div className="flex h-28 items-center justify-center text-sm text-gray-400">
                No data for this period.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-gray-100 bg-gray-50">
                    <tr>
                      {[
                        'Date',
                        'Total Rooms',
                        'Rooms Occupied',
                        'Occupancy %',
                      ].map((h) => (
                        <th
                          key={h}
                          className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {rows.map((r) => {
                      const pct = r.occupancy_pct ?? 0;
                      return (
                        <tr key={r.stay_date} className="hover:bg-gray-50/50">
                          <td className="px-4 py-2.5 font-medium text-gray-900">
                            {fmtDate(r.stay_date)}
                          </td>
                          <td className="px-4 py-2.5 text-gray-600">
                            {r.total_rooms ?? '—'}
                          </td>
                          <td className="px-4 py-2.5 text-gray-600">
                            {r.rooms_occupied}
                          </td>
                          <td className="px-4 py-2.5">
                            <span
                              className={`font-semibold ${pct >= 80 ? 'text-green-600' : pct >= 50 ? 'text-pos-primary' : 'text-gray-600'}`}
                            >
                              {pct}%
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {!loaded && !isLoading && (
        <div className="flex h-40 items-center justify-center rounded-2xl border border-dashed border-gray-200 text-sm text-gray-400">
          Select a date range and click &ldquo;Run report&rdquo; to load data.
        </div>
      )}
    </div>
  );
};
